import React, { useEffect, useMemo, useState } from 'react';
import { ShoppingCart, BarChart3 } from 'lucide-react';
import {
    useStoreOrders,
    useStoreDeliveryAdmin,
    useStoreTimeSlots,
    getCoupons,
    getStorePaymentMethodsAdmin,
} from '../../../hooks/useStoreApi';
import { DashboardMetrics } from './DashboardMetrics';
import { DashboardQuickActions } from './DashboardQuickActions';
import { StoreEditModal } from './StoreEditModal';
import { DashboardHero } from './DashboardHero';
import { SetupChecklist } from './SetupChecklist';

/**
 * Dashboard tab — first thing a store admin sees when they land on /store-admin.
 *
 * Renders, top to bottom:
 *   - calm greeting hero
 *   - setup checklist (collapsed by default; ribbon shows progress)
 *   - smart suggestion-of-the-day (silent if no rule applies)
 *   - metric tiles
 *   - quick-action grid
 *   - archived orders summary (when relevant)
 *
 * Snapshot fetching is centralised here so the checklist + suggestion engine
 * see one consistent picture of the store.
 */
export function DashboardTab({
    store,
    products,
    categories,
    refetchStore,
    refetchProducts,
    refetchCategories,
    onRegisterRefresh,
    chatId,
    onGoToTab,
}) {
    const [showEditModal, setShowEditModal] = useState(false);
    const storeId = store?.store_id;

    const { data: ordersData, loading: ordersLoading, refetch: refetchOrders } =
        useStoreOrders(storeId, null, chatId);

    const { deliveryPoints, deliveryAreas, refetch: refetchDelivery } =
        useStoreDeliveryAdmin(storeId, chatId);

    const { timeSlots, timeSlotsEnabled, refetch: refetchTimeSlots } =
        useStoreTimeSlots(storeId, chatId);

    // Coupons + payment methods don't have hooks today — fetch imperatively.
    const [couponsCount, setCouponsCount] = useState(null);
    const [paymentMethods, setPaymentMethods] = useState(null);

    const reloadCoupons = useMemo(
        () => async () => {
            if (!storeId) return;
            try {
                const coupons = await getCoupons(storeId, chatId);
                setCouponsCount(Array.isArray(coupons) ? coupons.length : 0);
            } catch {
                setCouponsCount(0);
            }
        },
        [storeId, chatId],
    );

    const reloadPayments = useMemo(
        () => async () => {
            if (!storeId) return;
            try {
                const res = await getStorePaymentMethodsAdmin(storeId, chatId);
                const methods = Array.isArray(res?.payment_methods) ? res.payment_methods : [];
                setPaymentMethods(methods);
            } catch {
                setPaymentMethods([]);
            }
        },
        [storeId, chatId],
    );

    useEffect(() => { reloadCoupons(); }, [reloadCoupons]);
    useEffect(() => { reloadPayments(); }, [reloadPayments]);

    const orders = ordersData?.orders || [];
    const activeOrders = orders.filter((o) => o.status !== 'archived');
    const archivedOrders = orders.filter((o) => o.status === 'archived');
    const pendingOrders = activeOrders.filter((o) => o.status === 'pending');

    const totalIncome = activeOrders.reduce(
        (sum, o) => sum + parseFloat(o.total || o.total_price || 0),
        0,
    );
    const archivedIncome = archivedOrders.reduce(
        (sum, o) => sum + parseFloat(o.total || o.total_price || 0),
        0,
    );

    const snapshot = useMemo(() => ({
        store,
        products,
        categories,
        deliveryPoints,
        deliveryAreas,
        timeSlots,
        timeSlotsEnabled,
        timeSlotsKnown: true,
        paymentMethods,
        couponsCount: couponsCount ?? 0,
        pendingOrdersCount: pendingOrders.length,
        totalOrdersCount: orders.length,
    }), [
        store, products, categories,
        deliveryPoints, deliveryAreas,
        timeSlots, timeSlotsEnabled,
        paymentMethods, couponsCount,
        pendingOrders.length, orders.length,
    ]);

    useEffect(() => {
        if (!onRegisterRefresh) return;
        onRegisterRefresh('dashboard', async () => {
            await Promise.all([
                refetchStore?.(),
                refetchProducts?.(),
                refetchCategories?.(),
                refetchOrders?.(),
                refetchDelivery?.(),
                refetchTimeSlots?.(),
                reloadCoupons(),
                reloadPayments(),
            ]);
        });
    }, [
        onRegisterRefresh,
        refetchStore, refetchProducts, refetchCategories, refetchOrders,
        refetchDelivery, refetchTimeSlots, reloadCoupons, reloadPayments,
    ]);

    return (
        <div className="space-y-6">
            <DashboardHero store={store} />

            <SetupChecklist
                snapshot={snapshot}
                storeId={storeId}
                onGoToTab={onGoToTab}
            />

            <DashboardMetrics
                products={products}
                activeOrders={activeOrders}
                categories={categories}
                totalIncome={totalIncome}
                ordersLoading={ordersLoading}
            />

            <DashboardQuickActions onGoToTab={onGoToTab} />

            {!ordersLoading && archivedOrders.length > 0 && (
                <div className="surface-card p-5 animate-slide-up">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="heading-sm">הזמנות בארכיון</h3>
                        <span className="text-xs text-ink-subtle">שומר על נתונים נקיים</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                        <div className="bg-surface-muted rounded-xl p-3 sm:p-4 border border-line-subtle">
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="text-2xs font-medium text-ink-muted tracking-wide flex-1 min-w-0">סה"כ בארכיון</div>
                                <span className="inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-surface text-ink-muted shrink-0">
                                    <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5" />
                                </span>
                            </div>
                            <div className="text-xl sm:text-2xl font-bold text-ink tabular-nums whitespace-nowrap overflow-hidden leading-tight">{archivedOrders.length}</div>
                        </div>
                        <div className="bg-surface-muted rounded-xl p-3 sm:p-4 border border-line-subtle">
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="text-2xs font-medium text-ink-muted tracking-wide flex-1 min-w-0">הכנסה מצטברת</div>
                                <span className="inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-surface text-ink-muted shrink-0">
                                    <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5" />
                                </span>
                            </div>
                            <div className="text-xl sm:text-2xl font-bold text-ink tabular-nums whitespace-nowrap overflow-hidden leading-tight">
                                ₪{archivedIncome.toLocaleString('he-IL', { maximumFractionDigits: 0 })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showEditModal && (
                <StoreEditModal
                    store={store}
                    onClose={() => setShowEditModal(false)}
                    onSave={async () => {
                        await refetchStore?.();
                        setShowEditModal(false);
                    }}
                />
            )}
        </div>
    );
}

export default DashboardTab;
